/**
 * Форма добавления/редактирования правила автозаявки — ОТДЕЛЬНЫЙ ЭКРАН с
 * «Назад» (BINDING: feedback_every_form_is_a_separate_screen). Рендерится
 * вместо списка; кнопки «Сохранить»/«Назад» снизу.
 *
 * Поля: триггер · категория (услуга) · расписание (каждый день / через N дней /
 * через N дней после последней выполненной) · область · отдел · приоритет.
 */
import { useMemo, useState } from 'react';
import { Button, Card } from '@hsc/ui';
import {
  adminListServiceCategories,
  type AutoTaskRule,
  type AutoTaskScope,
  type AutoTaskTrigger,
  type CreateAutoTaskRuleInput,
} from '@hsc/api-client';
import type { ServiceCategory } from '@hsc/types';
import { useQuery } from '@tanstack/react-query';
import { useStaffDepartments } from '../staff/useStaff.js';
import {
  useCreateAutoTaskRule,
  useUpdateAutoTaskRule,
} from './useAutoTasks.js';

const TRIGGER_LABELS: Readonly<Record<AutoTaskTrigger, string>> = {
  'stay.checked_out': 'При выселении гостя',
  'room.cleanliness_stale': 'Когда чистота устарела (порог)',
  'stay.checked_in': 'При заселении гостя',
  recurring_interval: 'Каждые N дней (планово)',
  n_days_after_last_completed: 'Через N дней после последней выполненной',
};

const SCOPE_LABELS: Readonly<Record<AutoTaskScope, string>> = {
  room: 'Каждый номер',
  stay: 'Каждое проживание',
  hotel: 'Весь отель',
};

const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
const PRIORITY_LABELS: Readonly<Record<string, string>> = {
  low: 'Низкий',
  normal: 'Обычный',
  high: 'Высокий',
  urgent: 'Срочный',
};

function isScheduled(t: AutoTaskTrigger): boolean {
  return t === 'recurring_interval' || t === 'n_days_after_last_completed';
}

function localized(text: unknown): string {
  if (typeof text === 'string') return text;
  if (text && typeof text === 'object') {
    const m = text as Record<string, string>;
    return m.ru ?? m.en ?? Object.values(m)[0] ?? '';
  }
  return '';
}

export interface AutoTaskRuleFormProps {
  /** Когда задано — редактируем существующее правило (триггер неизменен). */
  rule?: AutoTaskRule;
  onBack: () => void;
}

export function AutoTaskRuleForm({ rule, onBack }: AutoTaskRuleFormProps): JSX.Element {
  const editing = rule !== undefined;
  const deptsQ = useStaffDepartments();
  const catsQ = useQuery({
    queryKey: ['service-categories'],
    queryFn: async () => (await adminListServiceCategories()).items,
    staleTime: 60_000,
  });
  const create = useCreateAutoTaskRule();
  const update = useUpdateAutoTaskRule();

  const [trigger, setTrigger] = useState<AutoTaskTrigger>(rule?.trigger ?? 'recurring_interval');
  const [title, setTitle] = useState(rule?.title_template ?? '');
  const [dept, setDept] = useState(rule?.target_department ?? '');
  const [priority, setPriority] = useState(rule?.priority ?? 'normal');
  const [category, setCategory] = useState(rule?.category_code ?? '');
  const [intervalDays, setIntervalDays] = useState(rule?.interval_days ?? 1);
  const [scope, setScope] = useState<AutoTaskScope>(rule?.scope ?? 'room');

  const departments = deptsQ.data ?? [];
  const categories = catsQ.data ?? [];

  const canSubmit = useMemo(() => {
    if (title.trim() === '' || dept === '') return false;
    if (isScheduled(trigger) && intervalDays <= 0) return false;
    return true;
  }, [title, dept, trigger, intervalDays]);

  const busy = create.isPending || update.isPending;
  const errored = create.isError || update.isError;

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!canSubmit) return;
    const base = {
      target_department: dept,
      title_template: title.trim(),
      priority,
      ...(category !== '' ? { category_code: category } : {}),
      ...(isScheduled(trigger) ? { interval_days: intervalDays } : {}),
      scope,
    };
    if (editing && rule) {
      update.mutate(
        { id: rule.id, input: { ...base, enabled: rule.enabled } },
        { onSuccess: onBack },
      );
    } else {
      const input: CreateAutoTaskRuleInput = { trigger, ...base };
      create.mutate(input, { onSuccess: onBack });
    }
  }

  return (
    <section data-testid="auto-task-rule-form">
      <div className="fleet-drawer__head">
        <h1 className="hsc-page__title">
          {editing ? 'Правило автозаявки' : 'Новое правило автозаявки'}
        </h1>
      </div>
      <Card>
        <form className="hsc-stack" onSubmit={handleSubmit}>
          <label className="hsc-field">
            <span>Триггер</span>
            <select
              className="ta-input ta-select"
              value={trigger}
              disabled={editing}
              onChange={(e) => setTrigger(e.target.value as AutoTaskTrigger)}
              data-testid="atr-trigger"
            >
              {(Object.keys(TRIGGER_LABELS) as AutoTaskTrigger[]).map((t) => (
                <option key={t} value={t}>
                  {TRIGGER_LABELS[t]}
                </option>
              ))}
            </select>
          </label>

          <label className="hsc-field">
            <span>Заголовок заявки</span>
            <input
              className="ta-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Уборка номера {{room}}"
              data-testid="atr-title"
            />
          </label>

          <label className="hsc-field">
            <span>Категория (услуга)</span>
            <select
              className="ta-input ta-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="atr-category"
            >
              <option value="">По умолчанию (Хаускипинг)</option>
              {categories.map((c: ServiceCategory) => (
                <option key={c.id} value={c.code}>
                  {localized(c.name)}
                </option>
              ))}
            </select>
          </label>

          {isScheduled(trigger) && (
            <label className="hsc-field">
              <span>Период (дней)</span>
              <input
                className="ta-input"
                type="number"
                min={1}
                value={intervalDays}
                onChange={(e) => setIntervalDays(Number(e.target.value))}
                data-testid="atr-interval"
              />
            </label>
          )}

          <label className="hsc-field">
            <span>Область</span>
            <select
              className="ta-input ta-select"
              value={scope}
              onChange={(e) => setScope(e.target.value as AutoTaskScope)}
              data-testid="atr-scope"
            >
              {(Object.keys(SCOPE_LABELS) as AutoTaskScope[]).map((s) => (
                <option key={s} value={s}>
                  {SCOPE_LABELS[s]}
                </option>
              ))}
            </select>
          </label>

          <label className="hsc-field">
            <span>Отдел-исполнитель</span>
            <select
              className="ta-input ta-select"
              value={dept}
              onChange={(e) => setDept(e.target.value)}
              data-testid="atr-department"
            >
              <option value="">— выберите отдел —</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>

          <label className="hsc-field">
            <span>Приоритет</span>
            <select
              className="ta-input ta-select"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              data-testid="atr-priority"
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABELS[p]}
                </option>
              ))}
            </select>
          </label>

          {errored && (
            <p role="alert" className="fleet-card__err">
              Не удалось сохранить правило. Проверьте поля и права доступа.
            </p>
          )}

          <div className="room-edit__footer">
            <Button type="submit" disabled={!canSubmit || busy} data-testid="atr-save">
              {busy ? 'Сохраняем…' : 'Сохранить'}
            </Button>
            <Button type="button" variant="secondary" onClick={onBack} data-testid="atr-back">
              Назад
            </Button>
          </div>
        </form>
      </Card>
    </section>
  );
}
